import {useCallback, useEffect, useRef, useState} from "react";
import {CardHeader} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useLocation, useSearchParams} from "react-router-dom";
import {PageContainer} from "../../components/common/page-container";
import {ContentContainer} from "../../components/common/content-container";
import useOrderQueue from "../../components/orders/use-order-queue";
import OrderQueueView from "../../components/orders/order-queue-view";
import {OrderShipmentsLink} from "../../components/orders/order-shipments";
import useOrderDecision from "../../components/orders/use-order-decision";
import OrderStatusMenu from "../../menu/order/order-status-menu";
import OrderActionsMenu from "../../menu/order/order-actions-menu";
import OrderStatusChangeDialog from "../../dialog/distributor/order/order-status-change-dialog";
import OrderApproveDialog from "../../dialog/distributor/order/order-approve-dialog";
import OrderRejectDialog from "../../dialog/distributor/order/order-reject-dialog";
import OrderDetailsDialogDistributor from "../../dialog/distributor/order/order-details-dialog-distributor";
import {exportToExcel, exportToPdf} from "../../utils/export-utils";
import {buildShippingAddress, getOrderProducts, getTenant} from "./order-api";
import GlowingMessage from "../../errors/glowing-message";
import useTransientMessage from "../../errors/use-transient-message";
import {eventBus} from "../../event-bus";
import useNotificationOrder from "../../notifications/use-notification-order";
import NotificationOrderFeedback from "../../notifications/notification-order-feedback";
import {OrderConfirmationLink} from "../../components/orders/order-confirmation-entry";

export default function MyOrdersDistributor({navOpen}) {
    const {t, i18n} = useTranslation(), location = useLocation(), queue = useOrderQueue("importer");
    const [params, setParams] = useSearchParams();
    const openRevision = useCallback(id => {const next = new URLSearchParams(params); next.set("approval", id); setParams(next);}, [params, setParams]);
    const [selected, setSelected] = useState(null), [dialog, setDialog] = useState(null), [mode, setMode] = useState("review");
    const [statusMenu, setStatusMenu] = useState(null), [actionsMenu, setActionsMenu] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const exportRequest = useRef(null);
    useEffect(() => () => exportRequest.current?.abort(), [queue.identity, i18n.language]);
    const {message, messageKey, showMessage: setMessage, dismiss} = useTransientMessage();
    const decision = useOrderDecision(`${queue.identity}:${i18n.language}:${selected?.orderId}:${dialog}`);
    const close = useCallback(() => {setDialog(null); setSelected(null);}, []);
    const open = useCallback((order, type) => {setSelected(order); setDialog(type);}, []);
    const openDetails = useCallback(order => open(order, "details"), [open]);
    const openShipments = useCallback(order => {const next = new URLSearchParams(params); next.set("shipments", order.orderId); setParams(next);}, [params, setParams]);
    const openActions = useCallback((event, order) => setActionsMenu({anchor: event.currentTarget, order}), []);
    const openStatus = useCallback((event, order) => setStatusMenu({anchor: event.currentTarget, order}), []);
    const approve = useCallback((order, value = "review") => {
        // A null ID from the queue explicitly identifies legacy rows; older notification DTOs may omit it.
        if (order.supplierOrderId === null) {setNewStatus("processing"); open(order, "status"); return;}
        setMode(value); open(order, "approve");
    }, [open]);
    const notificationOrder = useNotificationOrder(openDetails, close);
    const orderStatuses = {pending: {label: "Pending", color: "default"}, processing: {label: "Processing", color: "warning"},
        shipped: {label: "Shipped", color: "info"}, completed: {label: "Completed", color: "success"}, cancelled: {label: "Cancelled", color: "error"}};
    useEffect(() => {setDialog(null); setSelected(null); setStatusMenu(null); setActionsMenu(null); dismiss();}, [queue.identity, i18n.language, dismiss]);
    useEffect(() => {
        if ((new URLSearchParams(location.search).has("approval") || new URLSearchParams(location.search).has("shipments"))) {
            setDialog(null); setSelected(null); setStatusMenu(null); setActionsMenu(null);
        }
    }, [location.search]);
    const mutate = async (reason, _number, status, delivery) => {
        const success = await decision.submit(selected, {status: status.toUpperCase(), rejectionReason: reason || null,
            deliveryDate: delivery ? `${delivery}T12:00:00` : null, rejection: dialog === "reject"});
        if (success) {close(); setMessage(t("OrderQueueDecisionSaved")); queue.refresh();}
        return success;
    };
    const submitted = receipt => {
        close(); const summary = receipt.order.summary;
        setMessage(t(summary.confirmationRequired ? "ReviewSent" : summary.outcome === "REJECTED" ? "ReviewRejected" : "ReviewConfirmed"));
        eventBus.emit("refreshOrderPage");
    };
    const exportOrder = async (format, order) => {
        exportRequest.current?.abort(); const controller = new AbortController(); exportRequest.current = controller;
        try {
            const tenant = await getTenant({order, config: {signal: controller.signal}}), products = await getOrderProducts({order, config: {signal: controller.signal}});
            const address = buildShippingAddress({pharmacy: tenant});
            if (format === "pdf") {await exportToPdf(order, address, products, i18n.language, controller.signal);}
            else if (format === "excel") {await exportToExcel(order, address, products, i18n.language, controller.signal);}
        } catch (error) {if (!controller.signal.aborted) {setMessage(error.response?.data?.message || t("OrderQueueExportFailed"));}}
        if (!controller.signal.aborted) {setActionsMenu(null);}
    };
    return <>
        <GlowingMessage message={message} messageKey={messageKey} onClose={dismiss}/><OrderConfirmationLink/><OrderShipmentsLink/>
        <PageContainer navOpen={navOpen} sx={{flex: 1, minWidth: 0, width: "auto"}}>
            <NotificationOrderFeedback status={notificationOrder}/>
            <CardHeader title={t("Orders")} subheader={t("ReviewOrder")}/>
            <ContentContainer elevation={2}>
                <OrderQueueView queue={queue} onDetails={openDetails} onShipments={openShipments} onReview={approve}
                    onActions={openActions} onStatus={openStatus} statusMenuOrderId={statusMenu?.order?.orderId}
                    actionsMenuOrderId={actionsMenu?.order?.orderId}/>
            </ContentContainer>
        </PageContainer>
        <OrderStatusMenu anchorEl={statusMenu?.anchor} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)}
            order={statusMenu?.order} orderStatuses={orderStatuses} onStatusSelect={status => {
                const order = statusMenu.order; setStatusMenu(null);
                if (status.toUpperCase() === "PROCESSING") {approve(order, "all");}
                else {setNewStatus(status); open(order, "status");}
            }}/>
        <OrderActionsMenu anchorEl={actionsMenu?.anchor} open={Boolean(actionsMenu)} onClose={() => setActionsMenu(null)}
            order={actionsMenu?.order} onViewDetails={order => open(order, "details")} onApprove={approve}
            onQuickApprove={order => approve(order, "all")} onReject={order => open(order, "reject")} onExport={exportOrder}/>
        <OrderStatusChangeDialog key={`${selected?.orderId}:status`} open={dialog === "status"} onClose={close}
            order={selected} newStatus={newStatus} orderStatuses={orderStatuses} onConfirm={mutate}
            loading={decision.pending} error={decision.error} uncertain={decision.uncertain} timeZone={queue.data?.timeZone}/>
        <OrderRejectDialog key={`${selected?.orderId}:reject`} open={dialog === "reject"} onClose={close} order={selected}
            onConfirm={(reason, number) => mutate(reason, number, "CANCELLED", null)} loading={decision.pending}
            error={decision.error} uncertain={decision.uncertain}/>
        <OrderApproveDialog key={`${selected?.orderId}:${mode}`} quick={mode === "all"} open={dialog === "approve"}
            onClose={close} order={selected} onSubmitted={submitted}/>
        <OrderDetailsDialogDistributor open={dialog === "details"} onClose={close} order={selected} showActions
            onOpenRevision={openRevision}
            onExport={exportOrder} onApprove={approve} onQuickApprove={order => approve(order, "all")}
            onReject={order => open(order, "reject")}/>
    </>;
}
